import { baseApi } from './baseApi';

/**
 * Real referral tracking (service-user's ReferralController). The backend only tracks the code
 * and each redemption's PENDING/COMPLETED status — it does not know or store the referred user's
 * name (ReferralService.findMine returns raw referredUserId, not a joined profile), so the list
 * this powers shows status and dates, not names.
 */

/** Whether this account can still enter a friend's code. The server says only yes or no, never the limits. */
export interface ReferralRedeemState {
  canRedeem: boolean;
  reason: 'ALREADY_REDEEMED' | 'NOT_ELIGIBLE' | null;
  invitedByName: string | null;
}

export interface ReferralCode {
  id: string;
  userId: string;
  code: string;
  createdAt: string;
  /** Loyalty points each person earns when the invited friend's first booking is done. */
  rewardPoints?: number;
  /** Older servers do not send this. */
  redeem?: ReferralRedeemState;
}

export interface Referral {
  id: string;
  referrerId: string;
  referredUserId: string;
  status: 'PENDING' | 'COMPLETED';
  createdAt: string;
  completedAt: string | null;
  /** The friend's first name, when the server sends it. */
  friendName?: string | null;
}

export const referralApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Lazily created server-side on first call — see ReferralService.getOrCreateMyCode.
    getMyReferralCode: builder.query<ReferralCode, void>({
      query: () => '/referrals/my-code',
      providesTags: ['Referral'],
    }),
    getMyReferrals: builder.query<Referral[], void>({
      query: () => '/referrals/me',
      providesTags: ['Referral'],
    }),
    // Throws a 409 if this account already redeemed a code (once ever, enforced at the DB level)
    // or a 404 for an unknown code — both surface through the caller's existing error-toast path.
    redeemReferralCode: builder.mutation<Referral, { code: string }>({
      query: (body) => ({ url: '/referrals/redeem', method: 'POST', body }),
      invalidatesTags: ['Referral'],
    }),
  }),
});

export const { useGetMyReferralCodeQuery, useGetMyReferralsQuery, useRedeemReferralCodeMutation } = referralApi;
